import { useMemo } from 'react';
import type { PipingSystem } from '../types';
import type { summarizePipingSystems } from '../lib/exportSystemStatus';

export interface UseExportLiveSummaryDeps {
  systems: PipingSystem[];
  selected: Set<string>;
  loading: boolean;
  systemsStale: boolean;
  exporting: boolean;
  outputPath?: string;
  activeBatchSummary: ReturnType<typeof summarizePipingSystems>;
  exportSummary: ReturnType<typeof summarizePipingSystems>;
  accessibilityAnnouncement: string;
  quotaSummary?: string;
}

export function useExportLiveSummary(deps: UseExportLiveSummaryDeps): string {
  const {
    systems,
    selected,
    loading,
    systemsStale,
    exporting,
    outputPath,
    activeBatchSummary,
    exportSummary,
    accessibilityAnnouncement,
    quotaSummary = '',
  } = deps;

  const selectedSystemCount = useMemo(
    () =>
      systems.reduce(
        (count, system) => (selected.has(system.systemName) ? count + 1 : count),
        0
      ),
    [systems, selected]
  );

  return useMemo(() => {
    const loadedSummary = loading
      ? 'Refreshing piping systems.'
      : `${systems.length} system${systems.length === 1 ? '' : 's'} loaded${systemsStale ? '. Model changed; refresh systems when ready' : ''}.`;
    const validationSummary = exportSummary.notValidated > 0
      ? `${exportSummary.notValidated} system${exportSummary.notValidated === 1 ? '' : 's'} ${exportSummary.notValidated === 1 ? 'needs' : 'need'} validation.`
      : 'Validation status is current for the loaded systems.';
    const reviewSummary =
      `${exportSummary.ready} ready, ${exportSummary.warnings} warning${exportSummary.warnings === 1 ? '' : 's'}, ${exportSummary.errors} error${exportSummary.errors === 1 ? '' : 's'}.`;
    const batchSummary = selectedSystemCount > 0
      ? `${selectedSystemCount} system${selectedSystemCount === 1 ? '' : 's'} selected for the active batch.`
      : `The active batch is all ${systems.length} loaded system${systems.length === 1 ? '' : 's'}.`;
    const batchRisk = activeBatchSummary.notValidated > 0
      ? `${activeBatchSummary.notValidated} active batch system${activeBatchSummary.notValidated === 1 ? '' : 's'} ${activeBatchSummary.notValidated === 1 ? 'needs' : 'need'} validation.`
      : activeBatchSummary.errors > 0
        ? `${activeBatchSummary.errors} active batch error${activeBatchSummary.errors === 1 ? '' : 's'} need review.`
        : activeBatchSummary.warnings > 0
          ? `${activeBatchSummary.warnings} active batch warning${activeBatchSummary.warnings === 1 ? '' : 's'} need review.`
          : 'The active batch has no current warning or error status.';
    const destinationSummary = outputPath?.trim()
      ? `Destination is ${outputPath.trim()}.`
      : 'Destination will be chosen during export.';
    const exportStateSummary = exporting ? 'Export is running.' : 'Export is idle.';

    return [
      loadedSummary,
      validationSummary,
      reviewSummary,
      batchSummary,
      batchRisk,
      destinationSummary,
      exportStateSummary,
      quotaSummary,
      accessibilityAnnouncement,
    ].filter((sentence) => sentence.length > 0).join(' ');
  }, [
    accessibilityAnnouncement,
    activeBatchSummary.errors,
    activeBatchSummary.notValidated,
    activeBatchSummary.warnings,
    exporting,
    loading,
    outputPath,
    quotaSummary,
    exportSummary.errors,
    exportSummary.notValidated,
    exportSummary.ready,
    exportSummary.warnings,
    selectedSystemCount,
    systems.length,
    systemsStale,
  ]);
}
